import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './OpeningScreen.module.css';

// As chaves precisam bater com as refs do App.jsx
const MENU = [
  { key: 'homeRef',        tag: 'FASE 01', label: 'Início' },
  { key: 'sobreRef',       tag: 'FASE 02', label: 'Sobre mim' },
  { key: 'habilidadesRef', tag: 'FASE 03', label: 'Habilidades' },
  { key: 'projetosRef',    tag: 'FASE 04', label: 'Projetos' },
  { key: 'contatoRef',     tag: 'FASE 05', label: 'Contato' },
];

const BOOT_LINES = [
  '> iniciando portfolio.exe ...',
  '> carregando módulos: React ✔  Node ✔  PostgreSQL ✔',
  '> compilando experiência ............ ok',
  '> conectando ao servidor ............ ok',
  '> jogador detectado: VISITANTE',
  '> sistema pronto.',
];

const HUES = [185, 290, 320, 210, 160];
const rand = (a, b) => Math.random() * (b - a) + a;

const OpeningScreen = ({ onFinish }) => {
  const canvasRef = useRef(null);
  const speedRef = useRef(0.004);
  const pointerRef = useRef({ x: 0, y: 0 });
  const audioRef = useRef(null);
  const soundRef = useRef(false);
  const timerRef = useRef(null);

  const [phase, setPhase] = useState('boot'); // boot | title | menu
  const [lines, setLines] = useState(0);
  const [active, setActive] = useState(0);
  const [launching, setLaunching] = useState(false);
  const [sound, setSound] = useState(false);

  // ── Som (WebAudio, sem arquivos) ───────────────────────────────────────────
  const beep = useCallback((freq = 440, dur = 0.07, type = 'square', vol = 0.04) => {
    if (!soundRef.current) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!audioRef.current) audioRef.current = new AC();
      const ctx = audioRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(vol, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + dur);
    } catch { /* áudio é opcional */ }
  }, []);

  const toggleSound = (e) => {
    e.stopPropagation();
    const next = !soundRef.current;
    soundRef.current = next;
    setSound(next);
    if (next) beep(880, 0.08);
  };

  // ── Fluxo ──────────────────────────────────────────────────────────────────
  const begin = useCallback(() => {
    setPhase('menu');
    beep(660, 0.1);
  }, [beep]);

  const launch = useCallback((key) => {
    if (launching) return;
    setLaunching(true);
    [300, 420, 560, 760].forEach((f, i) => setTimeout(() => beep(f, 0.12, 'sawtooth'), i * 120));
    timerRef.current = setTimeout(() => onFinish?.(key), 1000);
  }, [launching, beep, onFinish]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Boot: revela as linhas e passa para o título
  useEffect(() => {
    if (phase !== 'boot') return undefined;
    let i = 0;
    let next;
    const id = setInterval(() => {
      i += 1;
      setLines(i);
      if (i >= BOOT_LINES.length) {
        clearInterval(id);
        next = setTimeout(() => setPhase('title'), 500);
      }
    }, 380);
    return () => { clearInterval(id); clearTimeout(next); };
  }, [phase]);

  // Velocidade do túnel conforme a fase
  useEffect(() => {
    speedRef.current = launching ? 0.09 : phase === 'title' ? 0.01 : phase === 'menu' ? 0.006 : 0.004;
  }, [phase, launching]);

  // Teclado
  useEffect(() => {
    const onKey = (e) => {
      if (launching || e.metaKey || e.ctrlKey || e.altKey) return;
      if (phase === 'boot') { setPhase('title'); return; }
      if (phase === 'title') { e.preventDefault(); begin(); return; }
      if (e.key === 'ArrowDown' || e.key === 's') {
        e.preventDefault();
        setActive((a) => (a + 1) % MENU.length);
        beep(520, 0.05);
      } else if (e.key === 'ArrowUp' || e.key === 'w') {
        e.preventDefault();
        setActive((a) => (a - 1 + MENU.length) % MENU.length);
        beep(520, 0.05);
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        launch(MENU[active].key);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, active, launching, begin, launch, beep]);

  // ── Túnel de estrelas (canvas) ─────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let w = 0, h = 0, raf, stars = [];
    const cam = { x: 0, y: 0 };
    let speed = 0.004;

    const makeStar = (fresh = false) => ({
      x: rand(-1, 1),
      y: rand(-1, 1),
      z: fresh ? 1 : rand(0.02, 1),
      hue: HUES[Math.floor(Math.random() * HUES.length)],
    });

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = '#05030f';
      ctx.fillRect(0, 0, w, h);
      stars = Array.from({ length: w < 768 ? 200 : 420 }, () => makeStar());
    };

    const onMove = (e) => {
      pointerRef.current = { x: (e.clientX / w - 0.5) * 2, y: (e.clientY / h - 0.5) * 2 };
    };

    const frame = () => {
      const target = reduce ? Math.min(speedRef.current, 0.0015) : speedRef.current;
      speed += (target - speed) * 0.06;
      cam.x += (pointerRef.current.x - cam.x) * 0.05;
      cam.y += (pointerRef.current.y - cam.y) * 0.05;

      ctx.fillStyle = 'rgba(5, 3, 15, 0.35)';
      ctx.fillRect(0, 0, w, h);

      const cx = w / 2;
      const cy = h / 2;
      const scale = Math.max(w, h) * 0.5;

      for (const s of stars) {
        const pz = s.z;
        s.z -= speed;
        if (s.z <= 0.02) { Object.assign(s, makeStar(true)); continue; }

        const px = cx + ((s.x - cam.x * 0.4) / pz) * scale * 0.5;
        const py = cy + ((s.y - cam.y * 0.4) / pz) * scale * 0.5;
        const sx = cx + ((s.x - cam.x * 0.4) / s.z) * scale * 0.5;
        const sy = cy + ((s.y - cam.y * 0.4) / s.z) * scale * 0.5;

        if (sx < -50 || sx > w + 50 || sy < -50 || sy > h + 50) { Object.assign(s, makeStar(true)); continue; }

        ctx.strokeStyle = `hsla(${s.hue}, 100%, 70%, ${1 - s.z * 0.6})`;
        ctx.lineWidth = (1 - s.z) * 2.2 + 0.3;
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(sx, sy);
        ctx.stroke();
      }
      raf = requestAnimationFrame(frame);
    };

    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('pointermove', onMove, { passive: true });
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onMove);
    };
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className={styles.root} role="dialog" aria-label="Tela de abertura do portfólio">
      <canvas ref={canvasRef} className={styles.canvas} aria-hidden="true" />
      <div className={styles.grid} aria-hidden="true" />
      <div className={styles.scan} aria-hidden="true" />
      <div className={styles.vignette} aria-hidden="true" />

      {/* HUD */}
      <div className={styles.hud}>
        <span className={styles.player}>P1 · DANILO</span>
        <div className={styles.hudRight}>
          <button type="button" className={styles.hudBtn} onClick={toggleSound} aria-pressed={sound}>
            SOM {sound ? 'ON' : 'OFF'}
          </button>
          {!launching && (
            <button
              type="button"
              className={styles.hudBtn}
              onClick={(e) => { e.stopPropagation(); launch('homeRef'); }}
            >
              PULAR ▸
            </button>
          )}
        </div>
      </div>

      <div className={`${styles.stage} ${launching ? styles.stageOut : ''}`}>
        {phase === 'boot' && (
          <div
            className={styles.terminal}
            onClick={() => setPhase('title')}
            role="button"
            tabIndex={0}
            aria-label="Pular inicialização"
          >
            {BOOT_LINES.slice(0, lines).map((l) => (
              <div key={l} className={styles.line}>{l}</div>
            ))}
            <div className={styles.bar}>
              <div
                className={styles.barFill}
                style={{ width: `${Math.round((lines / BOOT_LINES.length) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {phase !== 'boot' && (
          <>
            <h1 className={styles.title} data-text="DANILO BRAGA">DANILO BRAGA</h1>
            <p className={styles.subtitle}>DESENVOLVEDOR WEB · FULL-STACK</p>
          </>
        )}

        {phase === 'title' && (
          <button type="button" className={styles.start} onClick={begin}>
            ▶ TOQUE PARA COMEÇAR
          </button>
        )}

        {phase === 'menu' && (
          <>
            <ul className={styles.menu}>
              {MENU.map((m, i) => (
                <li
                  key={m.key}
                  className={`${styles.item} ${active === i ? styles.active : ''}`}
                  style={{ animationDelay: `${i * 70}ms` }}
                >
                  <button
                    type="button"
                    onMouseEnter={() => { setActive(i); beep(520, 0.04); }}
                    onFocus={() => setActive(i)}
                    onClick={() => launch(m.key)}
                  >
                    <span className={styles.tag}>{m.tag}</span>
                    <span>{m.label}</span>
                    <span className={styles.cursor} aria-hidden="true">▶</span>
                  </button>
                </li>
              ))}
            </ul>
            <p className={styles.hint}>
              <span className={styles.hintDesktop}>↑ ↓ navegar · ENTER selecionar</span>
              <span className={styles.hintTouch}>toque numa fase para entrar</span>
            </p>
          </>
        )}
      </div>

      <div className={`${styles.flash} ${launching ? styles.flashOn : ''}`} aria-hidden="true" />
    </div>
  );
};

export default OpeningScreen;